import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import AppTextField from "../../../../../components/common/forms/AppTextField";
import AppDatePicker from "../../../../../components/common/forms/AppDatePicker";
import AppSelect from "../../../../../components/common/forms/AppSelect";
import { useGetEquipmentsQuery } from "../../../../../hooks/useEquipments";
import { appColors } from "../../../../../theme";
import { Box, Grid, Switch, Typography } from "@mui/material";
import { SRFormFields } from "./utils/types";
import { ESRApprovalInitialValues } from "./utils/schema";
import ESRManpowerTable from "./ESR-Manpower/ESRManpowerTable";
import ESRManpowerForm from "./ESR-Manpower/ESRManpowerForm";
import ESREquipmentServicesTable from "./ESR-EquipmentService/ESREquipmentServiceTable";
import ESREquipmentServiceForm from "./ESR-EquipmentService/ESREquipmentServiceForm";
import LoadingIndicator from "../../../../../components/common/LoadingIndicator";
import { useESRApprovalContext } from "../Context/ESRApprovalContext";
import WarrantyJobConfirmDialog from "./WarrantyJobConfirmDialog";
import { useSnackBar } from "../../../../../components/ToastProvider";
import { useGetAllPMSCodeTaskPartsByPmCodeId } from "../../../../../hooks/usePMSCodeTaskParts";
import { useEJOESRApprovalContext } from "../Context/EJOESRApprovalContext";
import { ESREquipmentServiceColumns } from "./ESR-EquipmentService/utils/types";

const ESRCreationForm = () => {
  const { showSnackBar } = useSnackBar();
  const { state } = useLocation();
  const { ejoData } = state;
  const { id: paramsId } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const equipmentData = useGetEquipmentsQuery();

  const { currentSRSelected } = useEJOESRApprovalContext();
  const { data: pmsCodeTasks, isLoading: isPmCodeTasksLoading } =
    useGetAllPMSCodeTaskPartsByPmCodeId(currentSRSelected.pmCodeId);
  
  const {
    esrManpowerArray,
    setEsrManpowerArray,
    esrEquipmentServicesArray,
    setEsrEquipmentServicesArray,
    esrArray,
    setEsrArray,
  } = useESRApprovalContext();

  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<SRFormFields>({
    defaultValues: ESRApprovalInitialValues,
    mode: "onChange",
  });

  const [openWarrantyJobDialog, setOpenWarrantyJobDialog] =
    useState<boolean>(false);
  const [isWarrantyJob, setIsWarrantyJob] = useState<boolean>(false);

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const equipmentOptions: any[] =
    equipmentData?.data === undefined
      ? []
      : equipmentData.data.map((e) => {
          return {
            id: e.id,
            label: e.equipmentNo,
            value: e.id,
          };
        });

  const onSubmit = (esrData: any) => {

    if (esrManpowerArray.length === 0) {
      showSnackBar("Manpower & Man Hour is required", "error");
      return;
    }

    if (esrEquipmentServicesArray.length === 0) {
      showSnackBar("Task & Part Number List is required", "error");
      return;
    }

    const esr = {
      id: paramsId,
      isWarrantyJob: esrData.isWarrantyJob,
      esrManHourList: esrManpowerArray,
      equipmentServiceList: esrEquipmentServicesArray,
      esrNotes: esrData.esrNotes
    };

    const updatedEsr = esrArray.some((item) => item.id === esr.id)
      ? esrArray.map((item) => (item.id === esr.id ? esr : item))
      : [...esrArray, esr];
    setEsrArray(updatedEsr);
    backTo();
  };

  const backTo = () => {
    //* To clear the fields
    reset();
    navigate(`/${pathSegments[0]}/${pathSegments[1]}/edit/${ejoData.id}`, { state: { referrer: 'create'}});
  };

  useEffect(() => {
    reset();
  }, []);

  useEffect(() => {
    if (paramsId && currentSRSelected) {
      const currentEsr = esrArray.find((esr) => esr.id === paramsId);

      setValue("equipmentId", ejoData.equipmentId);

      setIsWarrantyJob(currentEsr?.isWarrantyJob ?? false);

      setEsrManpowerArray(currentEsr?.esrManHourList ?? []);

      if (!pmsCodeTasks) {
        setEsrEquipmentServicesArray(currentEsr?.equipmentServiceList ?? []);
      } else {
        const formattedEquipmentServicesArray = pmsCodeTasks.map(
          (pmsCodeTask, index): ESREquipmentServiceColumns => {
            return {
              id: String(index + 1),
              taskItem: pmsCodeTask.taskItem,
              taskTypeId: pmsCodeTask.taskTypeId,
              taskTypeItem: pmsCodeTask.taskTypeItem,
              partNumber: pmsCodeTask.partNumber,
              quantity: pmsCodeTask.quantity,
              remarks: "",
            };
          }
        );

        setEsrEquipmentServicesArray(formattedEquipmentServicesArray);
      }
      if (!currentEsr) {
        setOpenWarrantyJobDialog(true);
      }
    }
  }, [currentSRSelected, paramsId, setValue, pmsCodeTasks]);

  useEffect(() => {
    setValue("isWarrantyJob", isWarrantyJob);
  }, [isWarrantyJob]);

  return (
    <>
      <Stack direction="row" gap={2} justifyContent={"flex-end"} mb={1}>
        <Button
          sx={{ backgroundColor: appColors.lightGray, color: "black" }}
          onClick={backTo}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disableElevation
          variant="contained"
          sx={{ textTransform: "capitalize", borderRadius: 1.5 }}
        >
          Save
        </Button>
      </Stack>
      <Divider color="black" />
      <Grid container spacing={2} sx={{ marginTop: "1em" }}>
        <Grid container spacing={2} sx={{ marginLeft: ".14em" }}>
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="SR NO."
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              placeholder="0"
              value={currentSRSelected?.serviceRequestNo}
              disabled={true}
            />
          </Grid>
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="Business Unit"
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              placeholder="0"
              value={ejoData.businessUnit.name}
              disabled={true}
            />
          </Grid>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppDatePicker
            name="dateCreated"
            value={ejoData?.dateCreated ?? new Date()}
            onChange={() => {}}
            label="Date Created"
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            disabled={true}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Controller
            control={control}
            name="dateNeeded"
            render={({ field: { value, onChange } }) => (
              <AppDatePicker
                name="dateNeeded"
                value={ejoData.dateNeeded}
                onChange={onChange}
                label="Date Needed"
                labelAlignment="left"
                labelWidth="25%"
                fieldWidth="75%"
                fontWeight={400}
                error={!!errors.dateNeeded}
                helperText={errors.dateNeeded?.message}
                minDate={new Date()}
                disabled={true}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Controller
            control={control}
            name="equipmentId"
            render={({ field }) => (
              <AppSelect
                label="Equipment No."
                labelAlignment="left"
                labelWidth="25%"
                fieldWidth="75%"
                fontWeight={400}
                placeholder="Select here"
                error={!!errors.equipmentId}
                helperText={errors.equipmentId?.message}
                options={equipmentOptions}
                {...register("equipmentId")}
                {...field}
                disabled={true}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppTextField
            label="Service Requested"
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            placeholder="0"
            value={currentSRSelected?.serviceRequest}
            disabled={true}
          />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <AppTextField
            label={!currentSRSelected?.remarks ? "PM Code" : "Remarks"}
            labelAlignment="left"
            labelWidth="25%"
            fieldWidth="75%"
            fontWeight={400}
            placeholder="0"
            value={
              !currentSRSelected?.remarks
                ? currentSRSelected?.pmCode
                : currentSRSelected?.remarks
            }
            disabled={true}
          />
        </Grid>
        {pathSegments[2] == "create" && (
          <Grid item sm={12} md={12} lg={6}>
            <Controller
              control={control}
              name="isWarrantyJob"
              render={({ field: { value, onChange } }) => (
                <Stack direction="row" alignContent="center">
                  <Box sx={{ width: "25%" }}>
                    <Typography
                      color={appColors.formText}
                      whiteSpace="nowrap"
                      fontSize={15}
                      sx={{
                        paddingTop: ".5em",
                        marginRight: "2.5em",
                      }}
                    >
                      Warranty Job?
                    </Typography>
                  </Box>
                  <Box sx={{ width: "75%", display: "flex" }}>
                    <Switch
                      checked={getValues().isWarrantyJob}
                      {...register("isWarrantyJob")}
                      value={value}
                      onChange={onChange}
                    />
                    <Typography
                      color={appColors.formText}
                      whiteSpace="nowrap"
                      fontSize={15}
                      sx={{
                        paddingTop: ".5em",
                      }}
                    >
                      {!value ? "No" : "Yes"}
                    </Typography>
                  </Box>
                </Stack>
              )}
            />
          </Grid>
        )}

        {pathSegments[2] == "create" && (
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="Notes"
              labelAlignment="left"
              labelWidth="25%"
              fieldWidth="75%"
              fontWeight={400}
              placeholder=" "
              value={ currentSRSelected?.esrNotes }
              {...register("esrNotes")}
            />
          </Grid> 
        )}

        <Grid item xs={12}>
          <ESRManpowerTable />
        </Grid>
        <Grid item xs={12}>
          <ESREquipmentServicesTable />
        </Grid>
      </Grid>
      <ESRManpowerForm />
      <ESREquipmentServiceForm />
      <WarrantyJobConfirmDialog
        openWarrantyJobDialog={openWarrantyJobDialog}
        setOpenWarrantyJobDialog={setOpenWarrantyJobDialog}
        setIsWarrantyJob={setIsWarrantyJob}
      />
    </>
  );
};

export default ESRCreationForm;
